import AppForm, { AppFormValues } from 'apps/common/AppForm';
import DefaultPageLoader from 'common/components/DefaultPageLoader';
import ErrorComponent from 'common/components/Error';
import PageHeader from 'common/components/PageHeader';
import AuthenticatedLayout from 'common/layouts/AuthenticatedLayout';
import apps from 'common/services/apps';
import {
  AppLimitReachedOrExceededError,
  UpdateAppRequest,
  WebhookConflictError,
} from 'common/types/apps';
import { ApiError } from 'common/services/api';
import React, { useEffect } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

function EditApp() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as any;
  const webhookError = locationState?.webhookError;
  const conflict = locationState?.conflict;
  const getAppTask = useAsync(() => apps.getApp(params.id || 'asdf'), []);
  const app = getAppTask.result;

  // When we come from a failed webhook creation, focus the first webhook input once the form is rendered
  useEffect(() => {
    if (app && webhookError) {
      const el = document.getElementById('webhooks.0.url');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }
  }, [app, webhookError]);

  const toUpdateAppRequest = (formValues: AppFormValues): UpdateAppRequest => ({
    clientId: params.id || '',
    name: formValues.name,
    contacts: formValues.contacts.map((c) => c.value),
    redirectUrls: formValues.redirectUrls.map((r) => r.value),
    privacyPolicyUrl: formValues.privacyPolicyUrl,
    scopes: Object.entries(formValues.scopes)
      .filter(([, isChecked]) => isChecked)
      .map(([s]) => s),
    logoUrl: formValues.logoUrl,
    webhooks: formValues.webhooks
      .filter((webhook) => webhook.url && webhook.url.trim() !== '')
      .map((webhook) => ({
        id: webhook.id,
        url: webhook.url,
        model_version: webhook.model_version,
      })),
  });

  const updateAppTask = useAsyncCallback(async (v: AppFormValues) => {
    try {
      const updatedApp = await apps.updateApp(toUpdateAppRequest(v));
      navigate(`/apps/${updatedApp.clientId}`);
    } catch (e: any) {
      if (e instanceof AppLimitReachedOrExceededError) {
        throw new Error(
          "You've exceeded your app limit. To request an app increase, please submit a request via https://whoopinc.typeform.com/to/XmzituEp.",
        );
      } else if (e instanceof WebhookConflictError) {
        throw new Error(
          'A webhook with this URL already exists. Please use a different URL or update the existing one.',
        );
      } else if (e instanceof ApiError) {
        try {
          const bodyText = await e.response.text();
          const parsed = JSON.parse(bodyText);
          const hint = parsed?.error_hint || parsed?.error_description || e.message;
          throw new Error(hint);
        } catch (_) {
          throw new Error(e.message || 'Bad Request');
        }
      } else {
        throw new Error(e);
      }
    }
  });

  return (
    <>
      {getAppTask.loading && <DefaultPageLoader />}
      {getAppTask.error && (
        <ErrorComponent>{getAppTask.error.message}</ErrorComponent>
      )}
      {app && (
        <div>
          {webhookError && (
            <ErrorComponent>
              {conflict
                ? 'A webhook with this URL already exists. Please use a different URL or update the existing one.'
                : 'Unable to create webhook. Please verify the webhook details below and save the app.'}
            </ErrorComponent>
          )}
          <PageHeader title={`Edit: ${app.name}`} />
          <AppForm
            availableScopes={app.availableScopes}
            onSubmit={updateAppTask.execute}
            webhookError={!!webhookError}
            defaultValues={{
              contacts: app.contacts.map((c) => ({ value: c })),
              redirectUrls: app.redirectUrls.map((r) => ({ value: r })),
              name: app.name,
              privacyPolicyUrl: app.privacyPolicyUrl || '',
              scopes: app.scopes
                .filter((x) => x !== 'offline')
                .reduce((obj: Record<string, boolean>, current: string) => {
                  const newObj = { ...obj };
                  newObj[current] = true;
                  return newObj;
                }, {}),
              logoUrl: app.logoUrl,
              webhookId:
                app.webhooks.length > 0 ? app.webhooks[0].id : undefined,
              webhooks: app.webhooks.map((wh) => ({
                id: wh.id,
                url: wh.url,
                model_version: wh.model_version || 'v1',
              })),
            }}
            cta="Update App"
            loading={!!updateAppTask.loading}
            error={updateAppTask.error?.message}
          />
        </div>
      )}
    </>
  );
}

export default () => (
  <AuthenticatedLayout pageIdentifier="edit-app">
    <EditApp />
  </AuthenticatedLayout>
);
